import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';

const STAGE_LABELS = { verified: 'Lead Verified', visa_approved: 'Visa Approved', enrolled: 'Enrollment Complete' };
const STATUS_COLOR = { pending: '#f59e0b', approved: '#3b82f6', paid: '#10b981' };
const STATUS_ICON  = { pending: '⏳', approved: '✅', paid: '💰' };

export default function MyRewards() {
  const [rewards, setRewards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totals, setTotals]   = useState({ pending: 0, approved: 0, paid: 0 });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/referral/student/rewards/');
      setRewards(data);
      setTotals({
        pending:  data.filter(r => r.status === 'pending').reduce((s, r)  => s + r.amount, 0),
        approved: data.filter(r => r.status === 'approved').reduce((s, r) => s + r.amount, 0),
        paid:     data.filter(r => r.status === 'paid').reduce((s, r)     => s + r.amount, 0),
      });
    } catch {/* silent */} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="page-loading">Loading…</div>;

  const referrerRewards = rewards.filter(r => r.beneficiary === 'referrer');
  const referredRewards = rewards.filter(r => r.beneficiary === 'referred');

  return (
    <div className="dashboard">
      <div className="dash-welcome">
        <div>
          <h1>My Rewards</h1>
          <p>You earn ₹40,000 for every student you refer.</p>
        </div>
      </div>

      {/* Summary */}
      <div className="stats-grid">
        <div className="stat-card stat-pending">
          <div className="stat-value">₹{totals.pending.toLocaleString()}</div>
          <div className="stat-label">⏳ Pending Approval</div>
        </div>
        <div className="stat-card stat-approved">
          <div className="stat-value">₹{totals.approved.toLocaleString()}</div>
          <div className="stat-label">✅ Approved</div>
        </div>
        <div className="stat-card stat-paid">
          <div className="stat-value">₹{totals.paid.toLocaleString()}</div>
          <div className="stat-label">💰 Paid Out</div>
        </div>
        <div className="stat-card stat-earned">
          <div className="stat-value">₹{(totals.pending + totals.approved + totals.paid).toLocaleString()}</div>
          <div className="stat-label">Total Generated</div>
        </div>
      </div>

      {rewards.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">💸</div>
          <h3>No rewards yet</h3>
          <p>Rewards appear as your referred students progress through their admission journey.</p>
        </div>
      ) : (
        <>
          {/* Referral Rewards (for students you referred) */}
          {referrerRewards.length > 0 && (
            <div className="section-card">
              <h3>Referral Rewards — Students You Referred</h3>
              <p style={{ color: '#64748b', fontSize: '.88rem', marginBottom: '1rem' }}>
                You earn ₹40,000 for every student you refer.
              </p>
              <RewardTable rewards={referrerRewards} nameKey="for_student" nameLabel="Referred Student" />
            </div>
          )}

          {/* Enrollment Bonus (your own reward as a referred student) */}
          {referredRewards.length > 0 && (
            <div className="section-card">
              <h3>Your Enrollment Bonus</h3>
              <p style={{ color: '#64748b', fontSize: '.88rem', marginBottom: '1rem' }}>
                You receive ₹40,000 when your enrollment is confirmed.
              </p>
              <RewardTable rewards={referredRewards} nameKey="for_student" nameLabel="Reward For" showReferredBy />
            </div>
          )}
        </>
      )}
    </div>
  );
}

function RewardTable({ rewards, nameKey, nameLabel, showReferredBy }) {
  return (
    <div className="reward-table-wrap">
      <table className="reward-table">
        <thead>
          <tr>
            <th>{nameLabel}</th>
            {showReferredBy && <th>Referred By</th>}
            <th>Milestone</th>
            <th>Reward Type</th>
            <th>Amount</th>
            <th>Status</th>
            <th>Date</th>
            <th>Paid On</th>
          </tr>
        </thead>
        <tbody>
          {rewards.map(rw => (
            <tr key={rw.id}>
              <td>
                <div className="td-name">{rw[nameKey] || '—'}</div>
              </td>
              {showReferredBy && (
                <td><div className="td-name">{rw.referred_by || 'Self'}</div></td>
              )}
              <td>
                <span className={`stage-badge stage-${rw.stage}`}>
                  {STAGE_LABELS[rw.stage] || rw.stage}
                </span>
              </td>
              <td style={{ fontSize: '.82rem', color: '#64748b' }}>{rw.reward_type}</td>
              <td className="td-amount">₹{rw.amount.toLocaleString()}</td>
              <td>
                <span className="reward-status" style={{ color: STATUS_COLOR[rw.status] }}>
                  {STATUS_ICON[rw.status]} {rw.status.charAt(0).toUpperCase() + rw.status.slice(1)}
                </span>
              </td>
              <td>{rw.created_at ? new Date(rw.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</td>
              <td>{rw.paid_at   ? new Date(rw.paid_at).toLocaleDateString('en-IN',   { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
